import {useState, useMemo, useEffect} from 'react'
import {Download, Info, Loader2} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {Pagination} from '@/components/ui/pagination'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {endpoints} from '@/api/endpoints'
import {useI18n} from '@/i18n'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {MutedText} from '@/components/shared/MutedText'
import {ScrollableBox} from '@/components/shared/ScrollableBox'
import {toast} from '@/hooks/use-toast'
import type {SqlExecute} from './types'

interface ResultTableProps {
    result: SqlExecute | null
    queryId: string
    isRunning: boolean
}

const PAGE_SIZE_OPTIONS = [50, 100, 250, 500] as const

export function ResultTable({result, queryId, isRunning}: ResultTableProps) {
    const i18n = useI18n()
    const {hasPermission} = useAuth()
    const canExport = hasPermission(permissions.reporting.export)

    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(100)
    const [exporting, setExporting] = useState<string | null>(null)

    useEffect(() => { setPage(0) }, [result])

    const paginatedRows = useMemo(() => {
        if (!result) return []
        const start = page * pageSize
        return result.rows.slice(start, start + pageSize)
    }, [result, page, pageSize])

    const totalPages = result ? Math.max(1, Math.ceil(result.rows.length / pageSize)) : 1

    function handlePageSizeChange(newSize: number) {
        setPageSize(newSize)
        setPage(0)
    }

    async function handleExport(format: 'csv' | 'xlsx') {
        setExporting(format)
        try {
            /* eslint-disable i18next/no-literal-string -- URL construction and header parsing */
            const response = await fetch(
                `${endpoints.reporting.exportReport(queryId)}?format=${format}`,
                {credentials: 'include'}
            )
            if (!response.ok) {
                throw new Error(response.statusText)
            }
            const blob = await response.blob()
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            const disposition = response.headers.get('Content-Disposition')
            const filename = disposition?.match(/filename="?(.+?)"?$/)?.[1] ?? `export.${format}`
            /* eslint-enable i18next/no-literal-string */
            a.download = filename
            a.click()
            URL.revokeObjectURL(url)
            toast({title: i18n.reporting.exportSuccess as string})
        } catch (e) {
            toast({variant: 'destructive', description: e instanceof Error ? e.message : String(e)})
        } finally {
            setExporting(null)
        }
    }

    if (isRunning) {
        return (
            <InlineRow justify="center" gap="md" className="py-8">
                <Icon icon={Loader2} className="animate-spin" muted/>
                <MutedText>{i18n.reporting.running}</MutedText>
            </InlineRow>
        )
    }

    if (!result) {
        return <InlineEmptyText>{i18n.reporting.reportDetail.noResults}</InlineEmptyText>
    }

    return (
        <Stack gap="sm">
            {/* Truncation banner */}
            {result.truncated && (
                <InfoBanner variant="info" icon={Info}>
                    {i18n.reporting.reportDetail.truncatedBanner({shown: result.rows.length})}
                </InfoBanner>
            )}

            {/* eslint-disable i18next/no-literal-string -- export format labels */}
            {canExport && (
                <InlineRow gap="md" justify="end">
                    <Button
                        data-testid="btn-export-csv"
                        variant="outline"
                        size="sm"
                        onClick={() => handleExport('csv')}
                        disabled={!!exporting}
                    >
                        <Icon icon={exporting === 'csv' ? Loader2 : Download} size="sm" className={exporting === 'csv' ? 'animate-spin' : undefined}/>
                        CSV
                    </Button>
                    <Button
                        data-testid="btn-export-xlsx"
                        variant="outline"
                        size="sm"
                        onClick={() => handleExport('xlsx')}
                        disabled={!!exporting}
                    >
                        <Icon icon={exporting === 'xlsx' ? Loader2 : Download} size="sm" className={exporting === 'xlsx' ? 'animate-spin' : undefined}/>
                        XLSX
                    </Button>
                </InlineRow>
            )}
            {/* eslint-enable i18next/no-literal-string */}

            {/* Data table */}
            <ScrollableBox className="overflow-x-auto max-h-[500px]" data-testid="result-table">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {result.columns.map(col => (
                                <TableHead key={col.name} data-testid={`col-${col.name}`} className={`text-xs whitespace-nowrap`}>
                                    {col.label}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedRows.map((row, rowIdx) => (
                            <TableRow key={rowIdx}>
                                {result.columns.map(col => (
                                    <TableCell key={col.name} className={`text-xs whitespace-nowrap`}>
                                        {String(row[col.name] ?? '')}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </ScrollableBox>

            {/* Pagination */}
            {result.rows.length > PAGE_SIZE_OPTIONS[0] && (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={result.rows.length}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
                />
            )}

            {/* Row count summary */}
            <MutedText size="xs" align="right" block>
                {i18n.reporting.resultRows({count: result.rowCount})}
            </MutedText>
        </Stack>
    )
}
